import {
  Badge,
  Box,
  BoxProps,
  Button,
  Flex,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderMarkProps,
  SliderThumb,
  SliderTrack,
  Text,
} from '@chakra-ui/react';
import { IPlanDuration } from '@shared/types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RemoveAdd } from '../RemoveAdd';

export interface IPlanAdd extends IPlanDuration {
  planDurationId: string;
  quantity: number;
  title: string;
}

export interface IPriceCardProps extends Omit<BoxProps, 'onChange'> {
  title: string;
  description: string;
  plans: IPlanDuration[];
  onAdd: (item: IPlanAdd) => void;
}

export const PriceCard = ({
  title,
  description,
  plans,
  onAdd,
  ...props
}: IPriceCardProps) => {
  const { t } = useTranslation('checkout/common');

  const maxDuration = plans.reduce((max, plan) => {
    return Math.max(max, plan.duration);
  }, 0);

  const minDuration = plans.reduce((min, plan) => {
    return Math.min(min, plan.duration);
  }, maxDuration);

  const [duration, setDuration] = useState(minDuration);
  const [quantity, setQuantity] = useState(1);

  const currentDuration = plans.find(plan => plan.duration === duration);
  const tag = currentDuration?.tag;
  const handleChangeQuantity = (qnt: number) => {
    setQuantity(qnt);
  };

  const handleAdd = () => {
    const planByDuration = plans.find(plan => plan.duration === duration);
    const payload = {
      ...planByDuration,
      quantity,
      planDurationId: planByDuration?.id,
      title: title,
    };

    onAdd(payload as IPlanAdd);
    setQuantity(1);
  };

  const markStyles: Omit<SliderMarkProps, 'value'> = {
    fontWeight: 'semibold',
    fontSize: 'sm',
    display: 'flex',
    alignItems: 'end',
    textAlign: 'center',
    mt: '-12',
    ml: '-4',
    w: '12',
  };

  const typeSelector = useMemo(() => {
    if (tag) {
      return {
        borderColor: 'alert.800',
        colorScheme: 'alert',
      };
    }

    return { borderColor: 'brand.600' };
  }, [tag]);

  const typeDivision = {
    DAILY: 365,
    WEEKLY: 52,
    MONTHLY: 12,
    YEARLY: 1,
  };

  return (
    <Box
      bg="brand.600"
      color="white"
      p={8}
      borderRadius={12}
      boxShadow="xl"
      {...props}
      border={`4px solid`}
      borderColor={typeSelector.borderColor}
    >
      <Flex flexDir="column" alignItems="center" gap={6} h="full">
        {tag ? (
          <Badge
            colorScheme={typeSelector.colorScheme}
            textTransform="uppercase"
          >
            {tag}
          </Badge>
        ) : (
          <Box minH="26px" w="full" />
        )}
        <Text fontSize="2xl" fontWeight="semibold">
          {title}
        </Text>

        <Box w="full" px={4}>
          <Slider
            defaultValue={minDuration}
            min={minDuration}
            max={maxDuration}
            step={12}
            mt={8}
            colorScheme="brand"
            onChange={val => setDuration(val)}
            value={duration}
          >
            {plans.map(plan => (
              <SliderMark
                key={plan.id}
                value={plan.duration}
                {...markStyles}
                color={duration !== plan.duration ? 'brand.400' : 'white'}
                zIndex={1}
                sx={{
                  pointerEvents: 'auto !important',
                }}
                onClick={() => setDuration(plan.duration)}
              >
                <Text fontSize="2xl" cursor="pointer">
                  {plan.duration / typeDivision[plan.type]}
                </Text>
                <Text ml={0} mb={1}>
                  {plan.duration / typeDivision[plan.type] > 1
                    ? t('years')
                    : t('year')}
                </Text>
              </SliderMark>
            ))}

            <SliderTrack
              bg="neutral.100"
              h={2}
              borderRadius="full"
              border="1px solid white"
            >
              <SliderFilledTrack bg="brand.400" />
            </SliderTrack>
            <SliderThumb boxSize={6} bg="brand.400" border="2px solid white" />
          </Slider>
        </Box>

        <Text textAlign="center">
          {description.split(';').map(line => (
            <Text key={line}>{line}</Text>
          ))}
        </Text>

        <Flex
          flexDir="column"
          alignItems="center"
          h="full"
          w="full"
          justifyContent="end"
          gap={6}
        >
          <Flex flexDir="column" alignItems="center">
            <Flex align="baseline" gap={2}>
              <Text fontSize="sm">R$</Text>
              <Text fontSize="5xl" fontWeight="black">
                {plans.find(plan => plan.duration === duration)?.amountPrice}
              </Text>
              <Text fontSize="sm">/{t('month')}</Text>
            </Flex>
          </Flex>

          <Flex
            justifyContent="space-between"
            w="full"
            gap={4}
            alignItems="center"
          >
            <RemoveAdd
              size="sm"
              onChange={handleChangeQuantity}
              value={quantity}
            />
            <Button color="brand.500" onClick={handleAdd}>
              {t('add')}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};
